import { View, StyleSheet, TouchableOpacity, Platform, Image, Linking, Text } from "react-native";
import { BottomTabBar, createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { tabIconSize, FontAwesomeIcon, platform } from "../platform";
import {
  faCog,
  faTree,
  faPersonHiking,
  faCalendarDay,
  faEnvelope,
  faMountainSun,
} from "@fortawesome/free-solid-svg-icons";
import { COLORS } from "../constants/theme.js";
import { useIsFocused } from "@react-navigation/native";
import ParksNavigator from "./ParksNavigator.jsx";
import Events from "../pages/Events.jsx";
import Contacts from "../pages/Contacts.jsx";
import Settings from "../pages/Settings.jsx";
const treeBlack = require("../assets/images/tree_black.png");
import AppStore from "../storage/appStore.js";
import { logAnalyticsEvent, isLargeScreen } from "../platform";

//Data
const MainTab = createBottomTabNavigator();
//Functions

//Views
const CustomTabBarButton = ({ children, onPress }) => {
  const isSelected = useIsFocused();
  return (
    <TouchableOpacity
      style={styles.customTabBarButton}
      onPress={onPress}
      activeOpacity={0.8}
      android_ripple={{ borderless: true, color: "transparent" }}
      accessible={true}
      accessibilityLabel="Parks Tab"
      accessibilityRole="button"
      accessibilityHint="Navigates to the Parks screen"
    >
      <View style={[styles.customButton, isSelected && styles.glowButton]}>{children}</View>
    </TouchableOpacity>
  );
};

const tabBar = (props) => (
  <View>
    <BottomTabBar {...props} style={Platform.OS === "web" ? styles.webTabBar : styles.nativeTabBar} />
  </View>
);

const screenListeners = ({ navigation, route }) => ({
  state: (e) => {
    const currentRoute = e.data.state.routes[e.data.state?.index];
    if (currentRoute?.name === "Parks" && currentRoute.state?.index) {
      const subRoute = currentRoute.state.routes[currentRoute.state.index];
      AppStore.getState().setPageName(subRoute.name);
      logAnalyticsEvent("screen_view", { name: subRoute.name });
    } else {
      AppStore.getState().setPageName(currentRoute.name);
      logAnalyticsEvent("screen_view", { name: currentRoute.name });
    }
  },
});

const tabBarIcon = (routeName, color) => {
  let iconName;
  switch (routeName) {
    case "Parks":
      iconName = faTree;
      break;
    case "Trails":
      iconName = platform === "native" ? faMountainSun : faPersonHiking;
      break;
    case "Events":
      iconName = faCalendarDay;
      break;
    case "Contacts":
      iconName = faEnvelope;
      break;
    case "Settings":
      iconName = faCog;
      break;
  }

  return <FontAwesomeIcon icon={iconName} size={tabIconSize} color={color} />;
};

const tabBarButton = (navigation) => {
  return function ParksTabBarButton(props) {
    return (
      <CustomTabBarButton {...props} onPress={() => navigation.navigate("Parks", { screen: "ParksMap" })}>
        <Image source={treeBlack} style={styles.image} resizeMode="contain" />
      </CustomTabBarButton>
    );
  };
};

const tabItemsText = (title, color, focused) => (
  <Text
    style={{
      color,
      fontSize: 12,
      fontWeight: focused ? "600" : "400",
      marginLeft: isLargeScreen ? 20 : 0,
    }}
    maxFontSizeMultiplier={1.2}
  >
    {title}
  </Text>
);

const tabHeaderTitle = (title) => (
  <View>
    <Text style={styles.headerTitle} maxFontSizeMultiplier={1.2}>
      {title}
    </Text>
  </View>
);

//Main View
export default function Tabs() {
  return (
    <MainTab.Navigator
      initialRouteName="Parks"
      backBehavior="history"
      screenOptions={({ route }) => ({
        tabBarIcon: ({ color }) => tabBarIcon(route.name, color),
        tabBarActiveTintColor: "black",
        tabBarInactiveTintColor: "gray",
        tabBarLabelPosition: isLargeScreen ? "beside-icon" : "below-icon",
      })}
      screenListeners={screenListeners}
      tabBar={tabBar}
    >
      <MainTab.Screen
        name="Trails"
        component={Events} // Your Events component (can be a placeholder if just linking)
        options={{
          headerShown: true,
          tabBarLabel:
            platform === "native" ? ({ color, focused }) => tabItemsText("Trails", color, focused) : "Trails",
        }}
        listeners={{
          tabPress: (e) => {
            // Prevent default navigation behavior
            e.preventDefault();
            // Open the URL in the device's default browser
            Linking.openURL("https://vbtrails.virginiabeach.gov/");
          },
        }}
      />
      <MainTab.Screen
        name="Events"
        component={Events} // Your Events component (can be a placeholder if just linking)
        options={{
          headerShown: true,
          tabBarLabel:
            platform === "native" ? ({ color, focused }) => tabItemsText("Events", color, focused) : "Events",
        }}
        listeners={{
          tabPress: (e) => {
            // Prevent default navigation behavior
            e.preventDefault();
            // Open the URL in the device's default browser
            Linking.openURL(process.env.REACT_APP_VBGO_URL);
          },
        }}
      />
      <MainTab.Screen
        name="Parks"
        component={ParksNavigator}
        options={({ navigation }) => ({
          headerShown: false,
          tabBarButton: tabBarButton(navigation),
        })}
      />
      <MainTab.Screen
        name="Contacts"
        component={Contacts}
        options={{
          headerShown: true,
          title: platform === "native" ? tabHeaderTitle("Contacts") : "Contacts",
          tabBarLabel:
            platform === "native" ? ({ color, focused }) => tabItemsText("Contacts", color, focused) : "Contacts",
        }}
        // listeners={({ navigation }) => eventsTabPress(navigation)}
      />
      <MainTab.Screen
        name="Settings"
        component={Settings}
        options={{
          headerShown: true,
          title: platform === "native" ? tabHeaderTitle("Settings") : "Settings",
          tabBarLabel:
            platform === "native" ? ({ color, focused }) => tabItemsText("Settings", color, focused) : "Settings",
        }}
        // listeners={({ navigation }) => settingsTabPress(navigation)}
      />
    </MainTab.Navigator>
  );
}

//Styles
const styles = StyleSheet.create({
  screen: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  tabBarButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  webTabBar: {
    height: 60,
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.15,
    shadowRadius: 5,
    borderTopWidth: 0.5,
  },
  nativeTabBar: {
    backgroundColor: COLORS.textLight,
    paddingBottom: Platform.OS === "ios" ? 20 : 10,
    paddingTop: 10,
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    elevation: 20,
    shadowColor: COLORS.textDark,
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.15,
    shadowRadius: 5,
    borderTopColor: COLORS.border,
    borderTopWidth: 0.5,
  },
  customTabBarButton: {
    top: Platform.OS === "web" ? -35 : -25,
    justifyContent: "center",
    alignItems: "center",
  },
  customButton: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: COLORS.primary,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: COLORS.textDark,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 5,
  },
  image: {
    color: "white",
    width: 40,
    height: 40,
  },
  glowButton: {
    backgroundColor: COLORS.primary,
    borderWidth: 1.5,
    borderColor: COLORS.glowButtonBorder,
    shadowColor: COLORS.glowButtonBorder,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 20,
    ...Platform.select({
      android: {
        borderWidth: 1.5,
        borderColor: COLORS.glowButtonBorder,
        shadowColor: COLORS.glowButtonBorder,
        elevation: 10,
      },
    }),
  },
  headerTitle: {
    fontWeight: "bold",
    fontSize: 18,
  },
});
